import React, { useState, useEffect } from "react";

import Icon from "../../../components/AppIcon";
import Input from "../../../components/ui/Input";
import Button from "../../../components/ui/Button";
import { Checkbox } from "../../../components/ui/Checkbox";

import { sendEmail } from "../../../utils/emailService"; // EmailJS helper

const ApplicationSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeForm, setActiveForm] = useState("internship");
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    course: "",
    experience: "",
    motivation: "",
    agreeTerms: false,
    newsletter: true,
    college: "",
    year: "",
    branch: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const courses = [
    { value: "app-development", label: "App Development (iOS & Android)" },
    { value: "machine-learning", label: "Machine Learning & AI" },
    { value: "robotics", label: "Robotics & Hardware" },
    { value: "iot-development", label: "IoT Development" },
    { value: "web-development", label: "Full Stack Web Development" },
    { value: "career-consultation", label: "Need Career Consultation" },
  ];

  const experienceLevels = [
    { value: "beginner", label: "Complete Beginner" },
    { value: "some-knowledge", label: "Some Basic Knowledge" },
    { value: "intermediate", label: "Intermediate Level" },
    { value: "advanced", label: "Advanced (Looking to Specialize)" },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.3 }
    );

    const element = document.getElementById("apply-section");
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // submit using EmailJS helper
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.agreeTerms) {
      alert("Please agree to the terms and conditions to continue.");
      return;
    }
    setIsSubmitting(true);

    const templateParams = {
  formType: activeForm,               // 'internship' | 'course' | 'ambassador'
  fullName: formData.fullName || '',
  email: formData.email || '',
  phone: formData.phone || '',
  course: formData.course || '',
  experience: formData.experience || '',
  motivation: formData.motivation || '',
  agreeTerms: formData.agreeTerms ? 'Yes' : 'No',
  newsletter: formData.newsletter ? 'Yes' : 'No',
  college: formData.college || '',
  year: formData.year || '',
  branch: formData.branch || '',
  subject: '',                        // not used for application
  message: '',                        // not used for application
  timestamp: new Date().toISOString()
};

    try {
      await sendEmail(templateParams);
      setIsSubmitting(false);
      setSubmitSuccess(true);
      setTimeout(() => {
        setSubmitSuccess(false);
        setFormData({
          fullName: "",
          email: "",
          phone: "",
          course: "",
          experience: "",
          motivation: "",
          agreeTerms: false,
          newsletter: true,
          college: "",
          year: "",
          branch: "",
        });
      }, 3000);
    } catch (err) {
      setIsSubmitting(false);
      alert("There was an error submitting your application. Please try again.");
    }
  };

  if (submitSuccess) {
    return (
      <section id="apply-section" className="py-20 bg-gradient-to-b from-muted to-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-2xl border border-border p-12 text-center">
            <div className="w-20 h-20 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="CheckCircle" size={40} className="text-success" strokeWidth={2} />
            </div>
            <h2 className="text-3xl font-bold text-text-primary mb-4">Application Submitted Successfully!</h2>
            <p className="text-lg text-text-secondary mb-6">
              Thank you for your interest in Nipix Technology. Our team will review your application and contact you within 24 hours.
            </p>
            <div className="bg-primary/10 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-text-primary mb-3">What happens next?</h3>
              <div className="space-y-2 text-sm text-text-secondary">
                <div className="flex items-center space-x-2">
                  <Icon name="Clock" size={16} className="text-primary" strokeWidth={2} />
                  <span>Application review within 24 hours</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Phone" size={16} className="text-primary" strokeWidth={2} />
                  <span>Career counselor will call you for consultation</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Calendar" size={16} className="text-primary" strokeWidth={2} />
                  <span>Schedule your personalized course/internship plan</span>
                </div>
              </div>
            </div>
            <p className="text-sm text-text-secondary">
              Check your email for confirmation and next steps. Welcome to your career transformation journey!
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="apply-section" className="py-20 bg-gradient-to-b from-muted to-background">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-6">
            <span className="text-nipix-blue">S</span>
            <span className="text-text-primary">tart </span>
            <span className="text-nipix-blue">Y</span>
            <span className="text-text-primary">our </span>
            <span className="text-nipix-blue">T</span>
            <span className="text-text-primary">ransformation </span>
            <span className="text-nipix-blue">T</span>
            <span className="text-text-primary">oday</span>
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Take the first step towards your dream tech career. Apply for our internship program or enroll in industry-focused courses.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Form Selection & Benefits */}
          <div className="space-y-8">
            {/* Form Type Selection */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-border">
              <h3 className="text-xl font-bold text-text-primary mb-4">Choose Your Path</h3>
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => setActiveForm("internship")}
                  className={`w-full p-4 rounded-xl text-left transition-all duration-300 ${
                    activeForm === "internship" ? "bg-primary text-white shadow-lg" : "bg-surface text-text-secondary hover:bg-muted"
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <Icon name="Briefcase" size={20} strokeWidth={2} />
                    <div>
                      <div className="font-semibold">Internship</div>
                      <div className="text-sm opacity-75">₹0 Application Fee</div>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setActiveForm("course")}
                  className={`w-full p-4 rounded-xl text-left transition-all duration-300 ${
                    activeForm === "course" ? "bg-primary text-white shadow-lg" : "bg-surface text-text-secondary hover:bg-muted"
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <Icon name="BookOpen" size={20} strokeWidth={2} />
                    <div>
                      <div className="font-semibold">Course Enrollment</div>
                      <div className="text-sm opacity-75">Start Learning Today</div>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setActiveForm("ambassador")}
                  className={`w-full p-4 rounded-xl text-left transition-all duration-300 ${
                    activeForm === "ambassador" ? "bg-primary text-white shadow-lg" : "bg-surface text-text-secondary hover:bg-muted"
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <Icon name="Users" size={20} strokeWidth={2} />
                    <div>
                      <div className="font-semibold">Student Ambassador</div>
                      <div className="text-sm opacity-75">For College Students</div>
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {/* Benefits */}
            <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl p-6 border border-primary/20">
              <h3 className="text-xl font-bold text-text-primary mb-4 flex items-center">
                <Icon name="Star" size={20} className="mr-2 text-primary" strokeWidth={2} />
                {activeForm === "ambassador" ? "Ambassador Benefits" : "Why Choose Nipix?"}
              </h3>
              <div className="space-y-3">
                {(activeForm === "ambassador"
                  ? [
                      "Exclusive networking opportunities",
                      "Leadership development program",
                      "Monthly stipend and incentives",
                      "Direct mentorship from industry experts",
                      "Priority access to internships",
                      "Certificate of recognition",
                    ]
                  : [
                      "Industry mentors from top companies",
                      "Hands-on projects with real impact",
                      "Internship experience",
                      "Personalized career guidance",
                      "Lifetime alumni network access",
                    ]
                ).map((benefit, index) => (
                  <div key={`${activeForm}-benefit-${index}`} className="flex items-center space-x-3">
                    <Icon name="Check" size={16} className="text-success" strokeWidth={2} />
                    <span className="text-sm text-text-secondary">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Trust Indicators 
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-border">
              <h3 className="text-lg font-semibold text-text-primary mb-4">Trusted by Students</h3>
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-primary">2000+</div>
                  <div className="text-xs text-text-secondary">Students Placed</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-primary">50+</div>
                  <div className="text-xs text-text-secondary">Partner Companies</div>
                </div>
              </div>
            </div>*/}
          </div>

          {/* Application Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl shadow-2xl border border-border overflow-hidden">
              {/* Form Header */}
              <div className="bg-gradient-to-r from-primary to-secondary p-6 text-white">
                <div className="flex items-center space-x-3">
                  <Icon
                    name={activeForm === "internship" ? "Briefcase" : activeForm === "course" ? "BookOpen" : "Users"}
                    size={24}
                    strokeWidth={2}
                  />
                  <div>
                    <h3 className="text-xl font-bold">
                      {activeForm === "internship"
                        ? "Internship Application"
                        : activeForm === "course"
                        ? "Course Enrollment"
                        : "Student Ambassador Application"}
                    </h3>
                    <p className="text-sm opacity-90">
                      {activeForm === "internship"
                        ? "Apply for our internship program"
                        : activeForm === "course"
                        ? "Enroll in industry-focused technical courses"
                        : "Join our student ambassador program and lead your college community"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Form Content */}
              <form onSubmit={handleSubmit} className="p-8 space-y-6">
                {/* Personal Information */}
                <div className="grid md:grid-cols-2 gap-6">
                  <Input
                    label="Full Name"
                    type="text"
                    placeholder="Enter your full name"
                    value={formData.fullName}
                    onChange={(e) => handleInputChange("fullName", e.target.value)}
                    required
                    className="mb-0"
                  />

                  <Input
                    label="Email Address"
                    type="email"
                    placeholder="your.email@example.com"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    required
                    className="mb-0"
                  />
                </div>

                <Input
                  label="Phone Number"
                  type="tel"
                  placeholder="+91 9876543210"
                  value={formData.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  required
                />

                {/* Ambassador-specific fields */}
                {activeForm === "ambassador" && (
                  <>
                    <div className="grid md:grid-cols-2 gap-6">
                      <Input
                        label="College/University Name"
                        type="text"
                        placeholder="Enter your college name"
                        value={formData.college}
                        onChange={(e) => handleInputChange("college", e.target.value)}
                        required
                        className="mb-0"
                      />

                      <Input
                        label="Year of Study"
                        type="text"
                        placeholder="e.g., 2nd Year, Final Year"
                        value={formData.year}
                        onChange={(e) => handleInputChange("year", e.target.value)}
                        required
                        className="mb-0"
                      />
                    </div>

                    <Input
                      label="Course/Branch"
                      type="text"
                      placeholder="e.g., Computer Science, Electronics"
                      value={formData.branch}
                      onChange={(e) => handleInputChange("branch", e.target.value)}
                      required
                    />
                  </>
                )}

                {/* Course/Interest Selection */}
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    {activeForm === "internship" ? "Preferred Internship Track" : activeForm === "course" ? "Course Interest" : "Areas of Interest"}
                  </label>
                  <select
                    value={formData.course}
                    onChange={(e) => handleInputChange("course", e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors duration-250"
                  >
                    <option value="">Select your interest</option>
                    {courses.map((course) => (
                      <option key={course.value} value={course.value}>
                        {course.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Experience Level */}
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Current Experience Level
                  </label>
                  <select
                    value={formData.experience}
                    onChange={(e) => handleInputChange("experience", e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors duration-250"
                  >
                    <option value="">Select your level</option>
                    {experienceLevels.map((level) => (
                      <option key={level.value} value={level.value}>
                        {level.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Motivation */}
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    {activeForm === "ambassador" ? "Why do you want to become a Student Ambassador?" : "Why do you want to join Nipix Technology?"}
                  </label>
                  <textarea
                    value={formData.motivation}
                    onChange={(e) => handleInputChange("motivation", e.target.value)}
                    placeholder={activeForm === "ambassador" ? "Tell us about your leadership experience, community involvement, and how you plan to promote tech education in your college..." : "Tell us about your career goals and why you're interested in our program..."}
                    rows={4}
                    required
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors duration-250 resize-none"
                  />
                </div>

                {/* Checkboxes */}
                <div className="space-y-4">
                  <Checkbox
                    label="I agree to the Terms & Conditions and Privacy Policy"
                    checked={formData.agreeTerms}
                    onChange={(e) => handleInputChange("agreeTerms", e.target.checked)}
                    required
                  />

                  <Checkbox
                    label="Send me updates about new courses and career opportunities"
                    checked={formData.newsletter}
                    onChange={(e) => handleInputChange("newsletter", e.target.checked)}
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="default"
                  size="lg"
                  fullWidth
                  loading={isSubmitting}
                  disabled={!formData.agreeTerms}
                  iconName="Send"
                  iconPosition="right"
                  className="btn-primary text-lg py-4"
                >
                  {isSubmitting
                    ? "Submitting Application..."
                    : activeForm === "internship"
                    ? "Apply for Internship"
                    : activeForm === "course"
                    ? "Enroll in Course"
                    : "Apply as Ambassador"}
                </Button>

                {/* Form Footer */}
                <div className="text-center pt-4 border-t border-border">
                  <p className="text-sm text-text-secondary">
                    🔒 Your information is secure and will only be used for application processing.
                  </p>
                  <p className="text-xs text-text-secondary mt-2">
                    Our team will contact you within 24 hours to discuss your application.
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ApplicationSection;
